/**
 * IANA timezone helpers aligned with `MRGFunctions/functions/src/utils/timezone.js`
 * (balance sheet, inventory, Schedule C server logic).
 */

import type { User } from "src/interfaces/systemState.interface";

const _dtfCache = new Map<string, Intl.DateTimeFormat>();

/** True when `timeZone` is a valid IANA timezone supported by this runtime. */
export function isValidIanaTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== "string") return false;
  const tz = timeZone.trim();
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/** Browser-resolved timezone when valid, else UTC. */
export function getBrowserTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (isValidIanaTimeZone(tz)) return tz.trim();
  } catch {
    // ignore and fallback below
  }
  return "UTC";
}

/** Resolved IANA zone: user profile `timezone`, else browser, else UTC. */
export function getEffectiveTimeZoneFromUser(
  user: Pick<User, "timezone"> | null | undefined
): string {
  const stored = user?.timezone?.trim();
  if (isValidIanaTimeZone(stored)) return stored;
  return getBrowserTimeZone();
}

export function getDateTimePartsInTimeZone(date: Date, timeZone: string) {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return {
      year: NaN,
      month: NaN,
      day: NaN,
      hour: NaN,
      minute: NaN,
      second: NaN,
      millisecond: NaN,
    };
  }

  let dtf = _dtfCache.get(timeZone);
  if (!dtf) {
    dtf = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
    _dtfCache.set(timeZone, dtf);
  }

  const parts = dtf.formatToParts(date);
  const out: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== "literal") out[p.type] = p.value;
  }

  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    hour: Number(out.hour),
    minute: Number(out.minute),
    second: Number(out.second),
    millisecond:
      out.fractionalSecond !== undefined ? Number(out.fractionalSecond) : 0,
  };
}

function getOffsetMsAtInstant(timeZone: string, date: Date): number {
  const p = getDateTimePartsInTimeZone(date, timeZone);
  const ms =
    Number.isFinite(p.millisecond) && p.millisecond >= 0 && p.millisecond <= 999
      ? p.millisecond
      : date.getMilliseconds();
  const asUTC = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
    ms
  );
  return asUTC - date.getTime();
}

function pad2(n: number): string {
  return `${n}`.padStart(2, "0");
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True if `s` is a `yyyy-mm-dd` string (syntax only; may still be invalid calendar date). */
export function isYmdString(s: unknown): s is string {
  return typeof s === "string" && YMD_RE.test(s);
}

function parseYmdParts(
  ymd: string
): { year: number; month: number; day: number } | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return undefined;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    return undefined;
  }
  return { year, month, day };
}

/** First instant of civil `yyyy-mm-dd` in `timeZone`. Returns undefined if `ymd` is invalid. */
export function tryYmdStringToStartOfDayUtc(
  ymd: string,
  timeZone: string
): Date | undefined {
  const p = parseYmdParts(ymd);
  if (!p) return undefined;
  return zonedDateTimeToUtcDate(
    {
      ...p,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    },
    timeZone
  );
}

/** Last instant of civil `yyyy-mm-dd` in `timeZone`. Returns undefined if `ymd` is invalid. */
export function tryYmdStringToEndOfDayUtc(
  ymd: string,
  timeZone: string
): Date | undefined {
  const p = parseYmdParts(ymd);
  if (!p) return undefined;
  return zonedDateTimeToUtcDate(
    {
      ...p,
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 999,
    },
    timeZone
  );
}

/** `yyyy-mm-dd` for the calendar day of `date` in `timeZone`. */
export function calendarYmdInTimeZone(date: Date, timeZone: string): string {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }
  const { year, month, day } = getDateTimePartsInTimeZone(date, timeZone);
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
};

/**
 * UTC `Date` for a civil date-time interpreted in `timeZone`
 * (same algorithm as Cloud Functions `zonedDateTimeToUtcDate`).
 */
export function zonedDateTimeToUtcDate(
  parts: ZonedParts,
  timeZone: string
): Date {
  const utcGuess = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    parts.millisecond
  );
  let utc = utcGuess;

  const offset1 = getOffsetMsAtInstant(timeZone, new Date(utcGuess));
  utc = utcGuess - offset1;

  const offset2 = getOffsetMsAtInstant(timeZone, new Date(utc));
  if (offset2 !== offset1) {
    utc = utcGuess - offset2;
  }

  return new Date(utc);
}

/** Start of the calendar day containing `instant`, in `timeZone`. */
export function startOfCalendarDayContainingInstant(
  instant: Date,
  timeZone: string
): Date {
  const { year, month, day } = getDateTimePartsInTimeZone(instant, timeZone);
  return zonedDateTimeToUtcDate(
    {
      year,
      month,
      day,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    },
    timeZone
  );
}

/** End of the calendar day containing `instant`, in `timeZone`. */
export function endOfCalendarDayContainingInstant(
  instant: Date,
  timeZone: string
): Date {
  const { year, month, day } = getDateTimePartsInTimeZone(instant, timeZone);
  return zonedDateTimeToUtcDate(
    {
      year,
      month,
      day,
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 999,
    },
    timeZone
  );
}

/**
 * Minimum allowed dashboard/transaction range start:
 * always Jan 1 (00:00) of the prior calendar year in `timeZone`.
 */
export function getClampStartDateForTimeZone(timeZone: string): Date {
  const now = new Date();
  const parts = getDateTimePartsInTimeZone(now, timeZone);
  const year = parts.year - 1;
  return zonedDateTimeToUtcDate(
    {
      year,
      month: 1,
      day: 1,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    },
    timeZone
  );
}

/**
 * Default dashboard/transaction range start:
 * Jan 1 (00:00) of the prior year in Jan–Apr, else Jan 1 of current year.
 */
export function getDefaultDateRangeStartForTimeZone(timeZone: string): Date {
  const now = new Date();
  const parts = getDateTimePartsInTimeZone(now, timeZone);
  const isTaxSeason = parts.month <= 4;
  const year = parts.year - (isTaxSeason ? 1 : 0);
  return zonedDateTimeToUtcDate(
    {
      year,
      month: 1,
      day: 1,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    },
    timeZone
  );
}

export function getDefaultDateRangePeriodForTimeZone(timeZone: string): {
  start: Date;
  end: Date;
} {
  return {
    start: getDefaultDateRangeStartForTimeZone(timeZone),
    end: endOfCalendarDayContainingInstant(new Date(), timeZone),
  };
}

/** First instant of calendar month (month 1–12) in `timeZone`. */
export function startOfMonthInTimeZone(
  year: number,
  month: number,
  timeZone: string
): Date {
  return zonedDateTimeToUtcDate(
    {
      year,
      month,
      day: 1,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    },
    timeZone
  );
}

/** Last instant of calendar month (month 1–12) in `timeZone`. */
export function endOfMonthInTimeZone(
  year: number,
  month: number,
  timeZone: string
): Date {
  const lastDay = new Date(year, month, 0).getDate();
  return zonedDateTimeToUtcDate(
    {
      year,
      month,
      day: lastDay,
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 999,
    },
    timeZone
  );
}

/**
 * Date-only MUI picker bridge: show the stored-TZ civil day using a browser-local
 * `Date` at local noon (avoids most DST edge cases for date pickers).
 */
export function utcToDateOnlyPickerWallDate(utc: Date, timeZone: string): Date {
  if (!(utc instanceof Date) || isNaN(utc.getTime())) {
    return utc instanceof Date ? utc : new Date(NaN);
  }
  const p = getDateTimePartsInTimeZone(utc, timeZone);
  return new Date(p.year, p.month - 1, p.day, 12, 0, 0, 0);
}

/** Interpret picker's local Y/M/D (and noon) as civil time in `timeZone`. */
export function dateOnlyPickerWallDateToUtc(
  picker: Date,
  timeZone: string
): Date {
  if (!(picker instanceof Date) || isNaN(picker.getTime())) {
    return picker instanceof Date ? picker : new Date(NaN);
  }
  return zonedDateTimeToUtcDate(
    {
      year: picker.getFullYear(),
      month: picker.getMonth() + 1,
      day: picker.getDate(),
      hour: 12,
      minute: 0,
      second: 0,
      millisecond: 0,
    },
    timeZone
  );
}

/** Format a grid/table cell using `Intl` in the user's zone (no React). */
export function formatZonedDate(
  value: Date | null | undefined,
  timeZone: string,
  localeOrOptions: string | Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  },
  maybeOptions?: Intl.DateTimeFormatOptions
): string {
  if (value == null || !(value instanceof Date) || isNaN(value.getTime())) {
    return "";
  }
  const locale =
    typeof localeOrOptions === "string" ? localeOrOptions : "en-US";
  const options =
    typeof localeOrOptions === "string"
      ? maybeOptions || {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        }
      : localeOrOptions;
  return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(
    value
  );
}

export function zonedCalendarYearRange(
  year: number,
  timeZone: string
): { start: Date; end: Date } {
  return {
    start: startOfMonthInTimeZone(year, 1, timeZone),
    end: endOfMonthInTimeZone(year, 12, timeZone),
  };
}

export function zonedQuarterRange(
  year: number,
  quarter: 1 | 2 | 3 | 4,
  timeZone: string
): { start: Date; end: Date } {
  const startMonth = (quarter - 1) * 3 + 1;
  const endMonth = quarter * 3;
  return {
    start: startOfMonthInTimeZone(year, startMonth, timeZone),
    end: endOfMonthInTimeZone(year, endMonth, timeZone),
  };
}

/** `month0` is 0 (January) through 11 (December), matching `Date#getMonth`. */
export function zonedMonthRangeFromZeroIndexedMonth(
  year: number,
  month0: number,
  timeZone: string
): { start: Date; end: Date } {
  const m = month0 + 1;
  return {
    start: startOfMonthInTimeZone(year, m, timeZone),
    end: endOfMonthInTimeZone(year, m, timeZone),
  };
}

export function zonedTodayRange(timeZone: string): { start: Date; end: Date } {
  const now = new Date();
  return {
    start: startOfCalendarDayContainingInstant(now, timeZone),
    end: endOfCalendarDayContainingInstant(now, timeZone),
  };
}

function addCalendarDaysUtc(
  year: number,
  month: number,
  day: number,
  deltaDays: number
): { year: number; month: number; day: number } {
  const x = new Date(Date.UTC(year, month - 1, day + deltaDays));
  return {
    year: x.getUTCFullYear(),
    month: x.getUTCMonth() + 1,
    day: x.getUTCDate(),
  };
}

const WEEKDAY_SHORT_TO_NUM: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function getWeekdaySundayZeroInTimeZone(date: Date, timeZone: string): number {
  const w = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
  }).format(date);
  return WEEKDAY_SHORT_TO_NUM[w] ?? 0;
}

/**
 * Sunday–Saturday week in `timeZone` containing `instant`, matching legacy
 * browser-local `Date` week semantics used by the dashboard/transaction presets.
 */
export function zonedWeekRangeContainingInstant(
  instant: Date,
  timeZone: string
): { start: Date; end: Date } {
  const p = getDateTimePartsInTimeZone(instant, timeZone);
  const noonUtc = zonedDateTimeToUtcDate(
    {
      year: p.year,
      month: p.month,
      day: p.day,
      hour: 12,
      minute: 0,
      second: 0,
      millisecond: 0,
    },
    timeZone
  );
  const wd = getWeekdaySundayZeroInTimeZone(noonUtc, timeZone);
  const sun = addCalendarDaysUtc(p.year, p.month, p.day, -wd);
  const sat = addCalendarDaysUtc(sun.year, sun.month, sun.day, 6);
  return {
    start: zonedDateTimeToUtcDate(
      {
        year: sun.year,
        month: sun.month,
        day: sun.day,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      },
      timeZone
    ),
    end: zonedDateTimeToUtcDate(
      {
        year: sat.year,
        month: sat.month,
        day: sat.day,
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999,
      },
      timeZone
    ),
  };
}

/** Preset ranges (day / week / month / year) anchored in `timeZone` (“current” per that zone). */
export function zonedCurrentPresetPeriod(
  presetType: "day" | "week" | "month" | "year",
  timeZone: string
): { start: Date; end: Date } {
  const now = new Date();
  switch (presetType) {
    case "day":
      return zonedTodayRange(timeZone);
    case "week":
      return zonedWeekRangeContainingInstant(now, timeZone);
    case "month": {
      const p = getDateTimePartsInTimeZone(now, timeZone);
      return zonedMonthRangeFromZeroIndexedMonth(p.year, p.month - 1, timeZone);
    }
    case "year": {
      const p = getDateTimePartsInTimeZone(now, timeZone);
      return zonedCalendarYearRange(p.year, timeZone);
    }
  }
}
